<template>
  <v-container>
    <div class="elevation-2 px-5 mt-3">
      <SimplePageHeader title="Workflow Templates" class="px-4 pr-5 mb-5">
        <template v-slot:right-buttons>
          <ButtonCustomIcon
            icon="add"
            color="primary"
            text="Add"
            :routeName="workflowTemplateCreateRoute.name"
            :block="true"
          />
        </template>
      </SimplePageHeader>
      <v-row class="px-5 pb-2" align="center">
        <v-col cols="12" sm="6" md="4" lg="3" xl="2" class="pb-0">
          <BusinessEntitiesDropDownList
            v-model="businessEntityId"
            :clearable="true"
            @businessEntityIdChange="handleBusinessEntityChange"
          />
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="3" xl="2" class="pb-0">
          <ClientsDropDownList
            :id="clientId"
            :businessEntityId="businessEntityId"
            :clearable="true"
            @clientIdChange="clientId = $event"
          />
        </v-col>
        <v-col cols="12" sm="4" class="pb-0">
          <v-select
            v-model="typeId"
            :items="workflowTypeList"
            item-text="name"
            item-value="id"
            label="Type"
            clearable
            outlined
          />
        </v-col>
        <v-col cols="12" sm="4" class="pb-0">
          <v-switch
            v-model="isActive"
            :label="isActive ? 'Active' : 'Inactive'"
            class="mt-0"
            hide-details
            @change="loadTemplates"
          />
        </v-col>
      </v-row>
      <v-row justify="end">
      <v-col cols="12" md="6">
        <FilterButtons
          @search="loadTemplates"
          @clear="handleClearClick"
        ></FilterButtons>
      </v-col>
    </v-row>
    <FilterButtonsSmall
      @search="loadTemplates"
      @clear="handleClearClick"
    ></FilterButtonsSmall>
    </div>
    <v-container fluid class="px-0 pt-2 mt-4">
      <v-layout column>
        <v-data-table
          dense
          :headers="headers"
          :items="workflowTemplateList"
          :options.sync="options"
          :server-items-length="serverItemsLength"
          item-key="id"
          class="elevation-2"
        >
          <template v-slot:item.dealAssignmentTypeMatch="{ item }">
            {{ item.dealAssignmentTypeMatch ? DEAL_ASSIGNMENT_TYPES[item.dealAssignmentTypeMatch] : 'Any' }}
          </template>
          <template v-slot:item.contractTypeMatch="{ item }">
            {{ item.contractTypeMatch ? CONTRACT_TYPES[item.contractTypeMatch] : 'Any' }}
          </template>
          <template v-slot:item.isActive="{ item }">
            <v-simple-checkbox
              v-model="item.isActive"
              disabled
            />
          </template>
          <template v-slot:item.action="{ item }">
            <v-btn
              class="mx-1 my-1"
              color="primary"
              outlined
              @click="editTemplate(item)"
            >
              <v-icon>edit</v-icon>
            </v-btn>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  class="mx-1 my-1"
                  color="primary"
                  outlined
                  v-bind="attrs"
                  v-on="on"
                  @click="copyTemplate(item)"
                >
                  <v-icon>content_copy</v-icon>
                </v-btn>
              </template>
              <span>Copy Template</span>
            </v-tooltip>
            <v-tooltip v-if="item.isActive" bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  class="mx-1 my-1"
                  color="red"
                  outlined
                  v-bind="attrs"
                  v-on="on"
                  @click="deactivateTemplate(item)"
                >
                  <v-icon color="red">block</v-icon>
                </v-btn>
              </template>
              <span>Deactivate</span>
            </v-tooltip>
          </template>
        </v-data-table>
      </v-layout>
    </v-container>
    <ConfirmationModal
      title="Deactivate Template"
      :contentText="templateToDeactivate ? `Deactivate template &quot;${templateToDeactivate.name}&quot;? It will no longer appear in active templates.` : ''"
      :showModal="showDeactivateConfirmation"
      toolbarColor="primary"
      okText="Deactivate"
      @ok-click="handleDeactivateConfirmed"
      @cancel-click="showDeactivateConfirmation = false; templateToDeactivate = null"
    />
    <CopyTemplateDialog
      v-if="showCopyDialog"
      :template="selectedTemplate"
      @close="showCopyDialog = false"
      @copied="handleCopied"
    />
    <BottomSpacer />
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import { mapFields } from '@/vueHelpers/vuex'
import { workflowTemplateCreateRoute, workflowTemplateEditRoute } from '@/lib/constants/routes'
import { ButtonCustomIcon, BottomSpacer, FilterButtons, FilterButtonsSmall } from '@/components/buttons'
import { SimplePageHeader } from '@/components/pageComponents'
import { BusinessEntitiesDropDownList } from '@/components/businessEntities'
import { ClientsDropDownList } from '@/components/clients'
import CopyTemplateDialog from './CopyTemplateDialog'
import { ConfirmationModal } from '@/components/dialogs'
import api from '@/lib/api'
import { PATH_WORKFLOW_TEMPLATES } from '@/lib/constants/api'
import { DEAL_ASSIGNMENT_TYPES } from '@/lib/constants/dealAssignmentTypes'
import { CONTRACT_TYPES } from '@/lib/constants/contractTypes'
import { notifySuccess, notifyError } from '@/lib/notifications'
import { parseGridOptionsFromQuery, buildPaginationQueryParams, isAllRecordsSortOnlyChange, setOrDeleteQueryParam, hasQueryChanged } from '@/lib/pagingAndSorting'

export default {
  components: {
    ButtonCustomIcon,
    BottomSpacer,
    SimplePageHeader,
    BusinessEntitiesDropDownList,
    ClientsDropDownList,
    CopyTemplateDialog,
    ConfirmationModal,
    FilterButtons,
    FilterButtonsSmall
  },
  data () {
    return {
      workflowTemplateCreateRoute,
      DEAL_ASSIGNMENT_TYPES,
      CONTRACT_TYPES,
      showCopyDialog: false,
      selectedTemplate: null,
      showDeactivateConfirmation: false,
      templateToDeactivate: null,
      hasFetchedAllTemplates: false,
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ['name'],
        sortDesc: [false]
      },
      headers: [
        { text: 'Name', value: 'name', sortable: true },
        { text: 'Type', value: 'typeName', sortable: true, width: '12%' },
        { text: 'Client', value: 'clientName', sortable: true, width: '16%' },
        { text: 'Deal Type', value: 'dealAssignmentTypeMatch', sortable: true, width: '12%' },
        { text: 'Contract Type', value: 'contractTypeMatch', sortable: true, width: '12%' },
        { text: 'Active', value: 'isActive', align: 'center', sortable: false, width: '8%' },
        { text: 'Actions', value: 'action', align: 'center', sortable: false, width: '15%' }
      ]
    }
  },
  computed: {
    ...mapFields('workflowTemplates', ['workflowTemplateList']),
    ...mapFields('workflowTemplates', ['totalRecords']),
    ...mapFields('workflowTemplateFilters', ['businessEntityId', 'clientId', 'typeId', 'isActive']),
    ...mapFields('workflowTypes', ['workflowTypeList']),
    serverItemsLength () {
      return this.options.itemsPerPage === -1 ? undefined : this.totalRecords
    }
  },
  watch: {
    options: {
      handler (newOptions, previousOptions) {
        this.syncOptionsToQuery()
        if (!this.shouldFetchTemplates(newOptions, previousOptions)) return
        this.fetchTemplates()
      },
      deep: true
    }
  },
  methods: {
    ...mapActions('workflowTemplates', ['getWorkflowTemplates', 'getWorkflowTemplatesWithFiltersPaginated']),
    ...mapActions('workflowTypes', ['getWorkflowTypes']),
    getOptionsFromQuery () {
      return parseGridOptionsFromQuery(this.$route.query, this.options)
    },
    applyFiltersFromQuery () {
      const { businessEntityId, clientId, typeId, isActive } = this.$route.query
      const parsedBusinessEntityId = Number(businessEntityId)
      this.businessEntityId = Number.isInteger(parsedBusinessEntityId) && parsedBusinessEntityId > 0 ? parsedBusinessEntityId : null
      const parsedClientId = Number(clientId)
      this.clientId = Number.isInteger(parsedClientId) && parsedClientId > 0 ? parsedClientId : null
      const parsedTypeId = Number(typeId)
      this.typeId = Number.isInteger(parsedTypeId) && parsedTypeId > 0 ? parsedTypeId : null
      this.isActive = isActive !== 'false'
    },
    syncOptionsToQuery () {
      const { sortColumn, sortDirection, ...paginationParams } = buildPaginationQueryParams(this.options)
      const nextQuery = { ...this.$route.query, ...paginationParams }

      setOrDeleteQueryParam(nextQuery, 'sortColumn', sortColumn)
      setOrDeleteQueryParam(nextQuery, 'sortDirection', sortColumn ? sortDirection : null)
      setOrDeleteQueryParam(nextQuery, 'businessEntityId', this.businessEntityId)
      setOrDeleteQueryParam(nextQuery, 'clientId', this.clientId)
      setOrDeleteQueryParam(nextQuery, 'typeId', this.typeId)
      setOrDeleteQueryParam(nextQuery, 'isActive', this.isActive)

      if (hasQueryChanged(this.$route.query, nextQuery, ['pageNumber', 'pageSize', 'sortColumn', 'sortDirection', 'businessEntityId', 'clientId', 'typeId', 'isActive'])) {
        this.$router.replace({ query: nextQuery })
      }
    },
    getFiltersObject () {
      return {
        businessEntityId: this.businessEntityId,
        clientId: this.clientId,
        typeId: this.typeId,
        isActive: this.isActive
      }
    },
    fetchTemplates () {
      const { page, itemsPerPage, sortBy, sortDesc } = this.options
      const filters = this.getFiltersObject()

      if (itemsPerPage === -1) {
        this.getWorkflowTemplates(filters).then(templates => {
          this.hasFetchedAllTemplates = true
          this.workflowTemplateList = templates
        })
        return
      }

      this.hasFetchedAllTemplates = false
      this.getWorkflowTemplatesWithFiltersPaginated({
        ...filters,
        pageNumber: page,
        pageSize: itemsPerPage,
        sortColumn: sortBy && sortBy.length ? sortBy[0] : null,
        sortDirection: sortDesc && sortDesc.length ? (sortDesc[0] ? 'DESC' : 'ASC') : null
      }).then(templates => {
        this.workflowTemplateList = templates
      })
    },
    shouldFetchTemplates (newOptions, previousOptions) {
      return !isAllRecordsSortOnlyChange(newOptions, previousOptions, this.hasFetchedAllTemplates)
    },
    handleFilterChange () {
      this.options = { ...this.options, page: 1 }
    },
    handleBusinessEntityChange () {
      this.clientId = null
    },
    loadTemplates () {
      this.handleFilterChange()
    },
    editTemplate (template) {
      this.$router.push({ name: workflowTemplateEditRoute.name, params: { id: template.id } })
    },
    copyTemplate (template) {
      this.selectedTemplate = template
      this.showCopyDialog = true
    },
    handleCopied (newName) {
      this.showCopyDialog = false
      api.doPost({ path: `${PATH_WORKFLOW_TEMPLATES}/${this.selectedTemplate.id}/copy`, data: { name: newName } })
        .then(response => {
          if (!response.data) {
            notifyError(this.$store.dispatch, response.errorMessage || 'Unable to copy template')
            return
          }
          notifySuccess(this.$store.dispatch, 'Template copied')
          this.loadTemplates()
        })
        .catch(() => notifyError(this.$store.dispatch, 'Unable to copy template'))
    },
    deactivateTemplate (template) {
      this.templateToDeactivate = template
      this.showDeactivateConfirmation = true
    },
    handleDeactivateConfirmed () {
      this.showDeactivateConfirmation = false
      const updated = { ...this.templateToDeactivate, isActive: false }
      this.templateToDeactivate = null
      api.doPut({ path: PATH_WORKFLOW_TEMPLATES, data: updated })
        .then(response => {
          if (!response.data) {
            notifyError(this.$store.dispatch, 'Unable to deactivate template')
            return
          }
          notifySuccess(this.$store.dispatch, 'Template deactivated')
          this.loadTemplates()
        })
        .catch(() => notifyError(this.$store.dispatch, 'Unable to deactivate template'))
    },
    handleClearClick () {
      this.businessEntityId = null
      this.clientId = null
      this.typeId = null
      this.isActive = true
      this.loadTemplates()
    }
  },
  created () {
    this.getWorkflowTypes()
    this.applyFiltersFromQuery()
    this.options = {
      ...this.options,
      ...this.getOptionsFromQuery()
    }
    this.syncOptionsToQuery()
  }
}
</script>
